/**
 * DisclosurePackagePanel — officer-facing preview of the disclosure package.
 *
 * Ported from legacy SPFx `DisclosurePackagePanel.tsx`.  Shows the list of
 * resolved disclosures with signer roles, required-flag, and page/field
 * counts.  Collapses large packages; surfaces per-doc compile errors.
 *
 * REQ-167 phase 2 (Bill 2026-07-23): per-row **Preview** now fetches the
 * BUILT package's flattened bytes via GET /v3/signing/internal/document
 * (officer-gated; prior signatures overlaid; editable PDFs refused server-
 * side) and shows them in a sandboxed-iframe modal — the RDL
 * DocumentPreviewModal pattern. Rows are matched to the built manifest by
 * templateCode (+ ordinal for multi-instance templates).
 */
import { useEffect, useRef, useState } from 'react';
import type { OfficerPackageDocumentInfo } from '@retail-ops/shared-types';
import type { BuiltPackage, PackageDocument } from '../../disclosure/PackageBuilder';
import { Banner, Button, CollapsibleSection, ModalShell, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { PdfCanvasViewer } from '@/components/common/PdfCanvasViewer';

export interface DisclosurePackagePanelProps {
  pkg: BuiltPackage | null;
  loading: boolean;
  error: string | null;
  /** FSR clicked "Prepare / refresh package" — triggers PackageBuilder re-run. */
  onRefresh?: () => void;
  /**
   * Account parties on the cart — surfaced as a compact CIF strip at the top of
   * the package (WT-BACKLOG C4 / TechReq#21: "the CIF number should associate").
   * Names with no CIF (brand-new customers pre-commit) still show, sans CIF.
   * Account numbers are reserved server-side and not yet on the FE cart DTO — a
   * follow-up surfaces those here alongside the CIF.
   */
  parties?: ReadonlyArray<{ cifId?: string | null; name: string }>;
  /**
   * Reserved accounts on the cart — product label + the account number AcctIdGen
   * grabbed before signing (WT-BACKLOG C4 / TechReq#21: "it has already grabbed
   * new account numbers"). Empty before AcctIdGen runs.
   */
  accounts?: ReadonlyArray<{ label: string; acctId: string }>;
  /**
   * REQ-167 ph2 — cart id for the built-package preview route. When absent
   * (or the package hasn't been built yet), Preview buttons hide/explain.
   */
  cartId?: string;
  /**
   * BACKLOG-1750 (Bill 2026-10-06: "Isn't the package already built?"): the package the server
   * BUILT at approval. When present the panel lists exactly these documents — nothing is
   * re-resolved or recompiled — and Preview opens each one directly.
   */
  built?: ReadonlyArray<OfficerPackageDocumentInfo> | null;
}

/** The built package: what the signers will actually see. Collapsed by default (Bill: "Disclosures
 *  collapsed / expandable — today they take most of the screen"). */
function BuiltPackageView({
  built, cartId, parties, accounts, onRefresh,
}: {
  built: ReadonlyArray<OfficerPackageDocumentInfo>;
  cartId?: string;
  parties?: DisclosurePackagePanelProps['parties'];
  accounts?: DisclosurePackagePanelProps['accounts'];
  onRefresh?: () => void;
}): JSX.Element {
  const client = useApiClient();
  const [previewDoc, setPreviewDoc] = useState<{ label: string; blob: Blob } | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const toSign = built.filter((d) => d.requiresSignature !== false).length;
  const pages = built.reduce((n, d) => n + (d.pageCount ?? 0), 0);
  const meta = [
    `${built.length} document${built.length === 1 ? '' : 's'}`,
    `${toSign} to sign`,
    built.length - toSign > 0 ? `${built.length - toSign} to read` : null,
    pages > 0 ? `${pages} page${pages === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');

  const preview = async (d: OfficerPackageDocumentInfo): Promise<void> => {
    if (!cartId) return;
    setErr(null);
    setLoadingId(d.documentId);
    try {
      const blob = await client.signing.getOfficerDocumentBlob(cartId, d.documentId);
      setPreviewDoc({ label: d.label, blob });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Preview failed.');
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <CollapsibleSection
      title="Documents"
      iconColor="var(--pcb-blue)"
      meta={meta}
      defaultOpen={false}
      actions={onRefresh && (
        <button type="button" className="btn btn-link" onClick={onRefresh} style={{ fontSize: 11 }}>Refresh</button>
      )}
    >
      {(parties?.length ?? 0) > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', padding: 'var(--sp-3) var(--sp-6)', fontSize: 11, color: 'var(--ink-700)', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600, color: 'var(--ink-500)' }}>For</span>
          {parties!.map((p, i) => (
            <span key={`${p.cifId ?? 'new'}-${i}`} style={{ whiteSpace: 'nowrap' }}>
              <strong style={{ color: 'var(--pcb-blue-dark)' }}>{p.name}</strong>
              {p.cifId ? <> · <code style={{ fontFamily: 'var(--font-mono)' }}>CIF {p.cifId}</code></> : <span style={{ color: 'var(--ink-500)' }}> · new customer</span>}
            </span>
          ))}
        </div>
      )}
      {(accounts?.length ?? 0) > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', padding: 'var(--sp-3) var(--sp-6)', fontSize: 11, color: 'var(--ink-700)', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600, color: 'var(--ink-500)' }}>Accounts</span>
          {accounts!.map((a, i) => (
            <span key={`${a.acctId}-${i}`} style={{ whiteSpace: 'nowrap' }}>
              {a.label} · <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--pcb-blue-dark)' }}>#{a.acctId}</code>
            </span>
          ))}
        </div>
      )}
      {err && <div style={{ padding: 'var(--sp-2) var(--sp-6)' }}><Banner tone="warn">{err}</Banner></div>}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {built.map((d) => (
          <li key={d.documentId} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 'var(--sp-2) var(--sp-6)', padding: 'var(--sp-3) var(--sp-6)', borderTop: '1px solid var(--border-subtle)', fontSize: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
              <strong style={{ color: 'var(--pcb-blue-dark)' }}>{d.label}</strong>
              {d.requiresSignature === false ? <Pill variant="info">Read only</Pill> : <Pill variant="warn">Signature</Pill>}
              {(d.pageCount ?? 0) > 0 && <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>{d.pageCount} page{d.pageCount === 1 ? '' : 's'}</span>}
            </div>
            {cartId && (
              <Button variant="ghost" size="sm" onClick={() => { void preview(d); }} disabled={loadingId === d.documentId}>
                {loadingId === d.documentId ? 'Loading…' : 'Preview'}
              </Button>
            )}
          </li>
        ))}
      </ul>
      {previewDoc && (
        <ModalShell open onClose={() => setPreviewDoc(null)} title={previewDoc.label} size="xl">
          <PdfCanvasViewer source={previewDoc.blob} title={`Preview of ${previewDoc.label}`} height="72vh" />
        </ModalShell>
      )}
    </CollapsibleSection>
  );
}

function modeBanner(pkg: BuiltPackage): JSX.Element | null {
  if (pkg.mode === 'demo-preview') {
    return (
      <Banner tone="warn" title="Demo preview">
        Package contents shown are based on cart state + config rules. For the
        demo, assume every row in this panel WILL be prepared for signing when
        the officer clicks <strong>Start signing</strong>.
      </Banner>
    );
  }
  if (pkg.errors.length > 0) {
    return (
      <Banner tone="err" title="Compile issues">
        {pkg.errors.length} template{pkg.errors.length === 1 ? '' : 's'} failed
        to compile server-side.  Rows are still listed below; the signing start
        call will skip failed rows.
      </Banner>
    );
  }
  return null;
}

function DocumentRow({
  doc,
  onPreview,
  previewLoading,
}: {
  doc: PackageDocument;
  /** REQ-167 ph2 — present when the built-package preview route is available. */
  onPreview?: (doc: PackageDocument) => void;
  previewLoading?: boolean;
}): JSX.Element {
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 'var(--sp-2) var(--sp-6)',
        // Condensed row padding (C4 / TechReq#20 — "too much, requires immediate
        // scrolling"): tightened from sp-5 so long packages fit without a scroll.
        padding: 'var(--sp-3) var(--sp-6)',
        borderTop: '1px solid var(--border-subtle)',
        fontSize: 12,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <strong style={{ color: 'var(--pcb-blue-dark)' }}>{doc.title}</strong>
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--ink-600)' }}>
            {doc.templateCode}
          </code>
          {doc.required
            ? <Pill variant="warn">Required</Pill>
            : <Pill variant="info">Optional</Pill>}
          {doc.signatureRequirement === 'view-only' && <Pill variant="info">View-only</Pill>}
          {doc.isDemoPreview && <Pill variant="info">Demo preview</Pill>}
          {doc.compileError && <Pill variant="err">Compile failed</Pill>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <span>{doc.pageCount || '?'} page{doc.pageCount === 1 ? '' : 's'}</span>
          <span>{doc.fieldCount || 0} field{doc.fieldCount === 1 ? '' : 's'}</span>
          {doc.signerRoles.length > 0 && (
            <span>
              Signers: <strong>{doc.signerRoles.join(', ')}</strong>
            </span>
          )}
        </div>
        {doc.compileError && (
          <div style={{ fontSize: 11, color: 'var(--err-text)', marginTop: 4 }}>
            {doc.compileError}
          </div>
        )}
      </div>
      {onPreview && !doc.compileError && (
        <div style={{ alignSelf: 'center' }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPreview(doc)}
            disabled={!!previewLoading}
            title="Preview the built document exactly as the signer sees it"
          >
            {previewLoading ? 'Loading…' : 'Preview'}
          </Button>
        </div>
      )}
    </li>
  );
}

/** Primitive: disclosure package preview. */
export function DisclosurePackagePanel(props: DisclosurePackagePanelProps): JSX.Element {
  const { pkg, loading, error, onRefresh, parties, accounts, cartId, built } = props;
  const client = useApiClient();

  // ── REQ-167 ph2 — built-document preview state ───────────────────────────
  const [previewDoc, setPreviewDoc] = useState<{ label: string; blob: Blob } | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const manifestRef = useRef<OfficerPackageDocumentInfo[] | null>(null);

  // A package rebuild ("Regenerate documents") invalidates the cached
  // manifest — re-fetch on the next Preview click.
  useEffect(() => {
    manifestRef.current = null;
  }, [pkg]);

  const closePreview = (): void => {
    setPreviewDoc(null);
  };

  const onPreview = async (doc: PackageDocument): Promise<void> => {
    if (!cartId || !pkg) return;
    setPreviewError(null);
    setPreviewLoadingId(doc.instanceId);
    try {
      if (!manifestRef.current) {
        const res = await client.signing.getOfficerPackageManifest(cartId);
        if (!res.success || !res.data) {
          throw new Error(res.error ?? 'The built package is not available yet.');
        }
        manifestRef.current = [...res.data];
      }
      // Match this resolved row to a BUILT document by templateCode; for
      // multi-instance templates (e.g. one signature card per account) use
      // the row's ordinal within its templateCode group.
      const siblings = pkg.documents.filter((d) => d.templateCode === doc.templateCode);
      const ordinal = Math.max(0, siblings.findIndex((d) => d.instanceId === doc.instanceId));
      const candidates = manifestRef.current.filter(
        (m) => (m.templateCode ?? '') === doc.templateCode,
      );
      const target = candidates[ordinal] ?? candidates[0];
      if (!target) {
        throw new Error(
          'This document is not in the built package yet — it becomes previewable once the package is generated at submit.',
        );
      }
      const blob = await client.signing.getOfficerDocumentBlob(cartId, target.documentId);
      setPreviewDoc({ label: doc.title, blob });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Preview failed.';
      // 404 PACKAGE_NOT_READY reads terribly raw — translate to plain language.
      setPreviewError(/PACKAGE_NOT_READY|No built disclosure package|404/i.test(msg)
        ? 'The signing package hasn’t been generated yet — documents become previewable after Approve Cart builds the package.'
        : msg);
    } finally {
      setPreviewLoadingId(null);
    }
  };

  if (built && built.length > 0) {
    return (
      <BuiltPackageView
        built={built}
        {...(cartId ? { cartId } : {})}
        {...(parties ? { parties } : {})}
        {...(accounts ? { accounts } : {})}
        {...(onRefresh ? { onRefresh } : {})}
      />
    );
  }

  if (loading) {
    return (
      <CollapsibleSection title="Documents" iconColor="var(--pcb-blue)" meta="Loading…" defaultOpen={false}>
        <div style={{ fontSize: 12, color: 'var(--ink-600)', padding: 'var(--sp-4)' }}>
          Loading the documents…
        </div>
      </CollapsibleSection>
    );
  }

  if (error) {
    return (
      <CollapsibleSection title="Disclosure package" iconColor="var(--pcb-red)" defaultOpen>
        <Banner tone="err" title="Could not build package">{error}</Banner>
      </CollapsibleSection>
    );
  }

  if (!pkg) {
    return (
      <CollapsibleSection title="Disclosure package" iconColor="var(--pcb-blue)" defaultOpen>
        <Banner tone="info">
          Package not yet built.  Click <strong>Prepare package</strong> to resolve
          disclosures from the cart.
        </Banner>
      </CollapsibleSection>
    );
  }

  const { documents, totals, signers } = pkg;
  const metaParts = [
    `${totals.docs} doc${totals.docs === 1 ? '' : 's'}`,
    `${totals.pages} page${totals.pages === 1 ? '' : 's'}`,
    `${signers.length} signer${signers.length === 1 ? '' : 's'}`,
  ];

  return (
    <CollapsibleSection
      title="Disclosure package"
      iconColor="var(--pcb-blue)"
      meta={metaParts.join(' · ')}
      defaultOpen={false}
      actions={onRefresh && (
        <button
          type="button"
          className="btn btn-link"
          onClick={onRefresh}
          style={{ fontSize: 11 }}
        >
          Refresh
        </button>
      )}
    >
      {modeBanner(pkg)}

      {/* CIF strip (C4 / TechReq#21) — who this package is for + their CIF, one
          compact line so the FSR sees the association without scrolling. */}
      {parties && parties.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px 10px',
            alignItems: 'center',
            padding: 'var(--sp-3) var(--sp-6)',
            fontSize: 11,
            color: 'var(--ink-700)',
            background: 'var(--surface-muted, var(--ink-50, #f5f6f8))',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <span style={{ textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600, color: 'var(--ink-500)' }}>
            For
          </span>
          {parties.map((p, i) => (
            <span key={`${p.cifId ?? 'new'}-${i}`} style={{ whiteSpace: 'nowrap' }}>
              <strong style={{ color: 'var(--pcb-blue-dark)' }}>{p.name}</strong>
              {p.cifId
                ? <> · <code style={{ fontFamily: 'var(--font-mono)' }}>CIF {p.cifId}</code></>
                : <span style={{ color: 'var(--ink-500)' }}> · new customer</span>}
            </span>
          ))}
        </div>
      )}

      {/* Reserved account numbers (C4 / TechReq#21) — the accounts AcctIdGen
          already grabbed, shown before the customer signs. */}
      {accounts && accounts.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px 10px',
            alignItems: 'center',
            padding: 'var(--sp-3) var(--sp-6)',
            fontSize: 11,
            color: 'var(--ink-700)',
            background: 'var(--surface-muted, var(--ink-50, #f5f6f8))',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <span style={{ textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600, color: 'var(--ink-500)' }}>
            Accounts
          </span>
          {accounts.map((a, i) => (
            <span key={`${a.acctId}-${i}`} style={{ whiteSpace: 'nowrap' }}>
              {a.label} · <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--pcb-blue-dark)' }}>#{a.acctId}</code>
            </span>
          ))}
        </div>
      )}

      {previewError && (
        <div style={{ padding: 'var(--sp-2) var(--sp-6)' }}>
          <Banner tone="warn">{previewError}</Banner>
        </div>
      )}

      {documents.length === 0 ? (
        <Banner tone="info">
          No disclosures resolved.  Verify product + entity selection on the
          prior steps.
        </Banner>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {documents.map((doc) => (
            <DocumentRow
              key={doc.instanceId}
              doc={doc}
              {...(cartId ? { onPreview: (d: PackageDocument) => { void onPreview(d); } } : {})}
              previewLoading={previewLoadingId === doc.instanceId}
            />
          ))}
        </ul>
      )}

      {signers.length > 0 && (
        <div
          style={{
            borderTop: '1px solid var(--border-subtle)',
            padding: 'var(--sp-5) var(--sp-6)',
            fontSize: 11,
            color: 'var(--ink-700)',
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <strong>Signers:</strong>
          {signers.map((s) => (
            <Pill key={`${s.Role}-${s.Email}`} variant="info">
              {s.Role} · {s.Name || s.Email || '—'}
            </Pill>
          ))}
        </div>
      )}

      {/* REQ-167 ph2 — preview of the FLATTENED built doc, drawn by pdf.js (CHG-0097)
          (same bytes + prior-signature overlay the signer sees; RDL
          DocumentPreviewModal pattern). */}
      {previewDoc && (
        <ModalShell
          open
          onClose={closePreview}
          title={previewDoc.label}
          size="xl"
        >
          <PdfCanvasViewer source={previewDoc.blob} title={`Preview of ${previewDoc.label}`} height="72vh" />
        </ModalShell>
      )}
    </CollapsibleSection>
  );
}
